<template>
  <div class="flex flex-col h-full">
    <div class="mb-4 text-sm lg:text-xl">DAO Distribution</div>
    <div class="flex-1 min-w-full h-96">
      <div class="flex h-full" v-if="!loading">
        <div class="h-full flex-1 min-w-0 flex flex-col">
          <div class="flex-1 h-48 lg:h-full" ref="container">
            <VChart
              ref="chart"
              class="h-48 lg:h-full"
              :option="option"
              :key="width"
            ></VChart>
          </div>
          <MoveBarX v-model="state.xPoint" class="ml-14"></MoveBarX>
        </div>
        <MoveBarY
          v-model="state.yPoint"
          :style="{ height: 'calc(100 % -60px)', 'min-height': '160px' }"
        ></MoveBarY>
      </div>

      <div v-else class="w-full h-full flex justify-center items-center">
        <Spin></Spin>
      </div>
    </div>
  </div>
</template>

<script setup>
import { queryDaoChart, queryDaoChartEco } from '@/api/dao';
import Spin from '@/components/Spin.vue';
import { checkScreenSize, useReqByBool } from '@/hooks';
import { formatVal, abbrv_num } from '@/utils';
import { useElementSize } from '@vueuse/core';
import { ScatterChart } from 'echarts/charts';
import {
  GraphicComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { ref, computed, inject, onMounted, reactive } from 'vue';
import VChart from 'vue-echarts';
import { homeChains, homeTypes } from '../../constant';
import MoveBarX from '../MoveBarX.vue';
import MoveBarY from '../MoveBarY.vue';
const chart = ref(null);

const container = ref(null);
const { width, height } = useElementSize(container);
const isLargeScreen = ref(checkScreenSize());
const props = defineProps({
  ecoCode: String,
});

use([
  CanvasRenderer,
  ScatterChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  GraphicComponent,
]);

const homeState = inject('homeState');

const state = reactive({ list: [], xPoint: [0, 0.5], yPoint: [0, 0.8] });

const data = computed(() => state.list);

const xAxis = computed(() => {
  let minPoint = data.value[0]?.members;
  let maxPoint = data.value[0]?.members;

  data.value.forEach((x) => {
    maxPoint = Math.max(maxPoint, x?.members);
    minPoint = Math.min(minPoint, x?.members);
  });

  const detal = maxPoint - minPoint;

  return [
    Math.floor(minPoint + detal * state.xPoint[0]),
    Math.ceil(minPoint + detal * state.xPoint[1]),
  ];
});

const yAxis = computed(() => {
  let minPoint = data.value[0]?.treasury;
  let maxPoint = data.value[0]?.treasury;

  data.value.forEach((x) => {
    maxPoint = Math.max(maxPoint, x?.treasury);
    minPoint = Math.min(minPoint, x?.treasury);
  });

  const detal = maxPoint - minPoint;

  return [
    Math.floor(Math.max(minPoint + detal * (1 - state.yPoint[1]), 0)),
    Math.ceil((minPoint + detal * (1 - state.yPoint[0])) * 1.2),
  ];
});

const option = computed(() => {
  const series = {
    type: 'scatter',
    data: data.value.map((x) => [x.members, +x.treasury]),
    symbolSize: isLargeScreen.value ? 16 : 8,
    itemStyle: {
      color: '#FF558566',
      borderColor: '#FF5585FF',
    },
  };

  return {
    responsive: true,
    maintainAspectRatio: false,
    grid: {
      top: '30',
      left: '40',
      right: '30',
      bottom: '40',
    },
    xAxis: {
      axisLine: {
        lineStyle: {
          color: '#fff',
        },
      },
      nameRotate: isLargeScreen.value ? 0 : -90,
      splitLine: {
        show: false,
      },
      axisLabel: {
        color: '#FFFFFFB3',
        fontSize: 12,
        formatter: (value) => abbrv_num(value),
      },

      name: 'Members',
      nameLocation: isLargeScreen.value ? 'center' : 'end',
      // nameLocation: 'center',
      nameTextStyle: {
        fontSize: 12,
        color: '#FFFFFFB3',
      },
      min: xAxis.value[0],
      max: xAxis.value[1],
    },
    yAxis: {
      axisLabel: {
        color: '#FFFFFFB3',
        fontSize: 12,
        formatter: (value) => abbrv_num(value),
      },
      splitLine: {
        show: false,
      },
      axisLine: {
        lineStyle: {
          color: '#fff',
        },
      },
      name: 'Treasury',
      nameTextStyle: {
        fontSize: 12,
        color: '#FFFFFFB3',
      },
      min: yAxis.value[0],
      max: yAxis.value[1],
    },
    tooltip: {
      axisPointer: {
        type: 'cross',
        crossStyle: {
          color: '#FFFFFF66',
          type: 'solid',
        },
        label: {
          show: false,
          formatter: ({ axisDimension, value }) => {
            if (axisDimension == 'x') {
              return '';
            }
            return value;
          },
        },
      },
      borderColor: '#FFFFFF66',
      backgroundColor: '#1e1f21',
      textStyle: {
        color: '#fff',
        fontSize: 12,
      },
      formatter: (config) => {
        const { dataIndex } = config;

        const info = data.value[dataIndex];

        return `
        <div class="space-y-2">
          <div class="flex">
            <img src="${
              info.logo || require('@/assets/icons/nftnologo.avif')
            }" class=" w-6 h-6 rounded-full  mr-2" />
            ${info.name}
          </div>
          <div>
            Treasury: $${formatVal(info.treasury, { bit: 2, omit: true })}
          </div>
          <div>
            Members: ${formatVal(info.members, { omit: true })}
          </div>
          <div>
            NFT holders: ${formatVal(info.nftHolders, { omit: true })}
          </div>
          <div>
           FT holders: ${formatVal(info.ftHolders, { omit: true })}
          </div>

        </div>
       `;
      },
    },
    color: '#FF5585FF',
    series: [series],
  };
});

const { loadData, loading } = useReqByBool(async () => {
  const res = props.ecoCode
    ? await queryDaoChartEco(props.ecoCode)
    : await queryDaoChart(
        homeTypes[homeState.selectedType].value,
        homeChains[homeState.selectedChain].value,
        homeState.filterStatus,
      );
  state.list = res;
  state.yPoint = [0, 1];
  state.xPoint = [0, 1];
});

onMounted(loadData);
</script>

<style lang="scss" scoped></style>
